import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  MapPin,
  Calendar,
  MessageSquare,
  ArrowRight,
  Loader2,
  Linkedin,
  Twitter,
  ChevronDown,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import PageHero from "../components/shared/PageHero";
import { submitLead } from "../lib/api";
import { COUNTRIES, DEFAULT_COUNTRY } from "../data/countries";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    app_name: "",
    message: "",
  });
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryOpen, setCountryOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        ...form,
        phone: form.phone ? `${country.dial} ${form.phone}` : undefined,
        message: `${form.message}\n[Country: ${country.name}]`.trim(),
        source: "contact-page",
      });
      toast.success("Message received. We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", app_name: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Lumotis — Book a Free ASO Audit & Growth Call</title>
        <meta
          name="description"
          content="Talk to Lumotis. Free ASO audit, growth consult, or just a hello. Email, Calendly booking, and message form available."
        />
        <link rel="canonical" href="https://lumotis.com/contact" />
      </Helmet>
      <Navbar />
      <main data-testid="contact-page">
        <PageHero
          testid="contact-hero"
          eyebrow="Contact"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Contact" }]}
          title="Let's talk about your"
          titleAccent="app's growth."
          subtitle="Drop us a note, book a 30-min call, or just email hello@lumotis.com. A real human (not a bot) replies within 24 hours."
        />

        <section className="py-16 md:py-20 bg-zinc-50 border-y border-zinc-200">
          <div className="container grid lg:grid-cols-12 gap-10">
            {/* Form */}
            <form
              onSubmit={submit}
              className="lg:col-span-7 bg-white border border-zinc-200 p-8 md:p-10"
              data-testid="contact-form"
            >
              <h2 className="font-display text-3xl font-bold text-charcoal">
                Send us a message
              </h2>
              <p className="mt-2 text-zinc-600">
                Tell us about your app and goals — we'll come back with concrete
                next steps, not a sales pitch.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-overline text-zinc-500">Your name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                    data-testid="contact-input-name"
                    placeholder="Priya Iyer"
                    className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-overline text-zinc-500">Work email</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    data-testid="contact-input-email"
                    placeholder="you@yourapp.com"
                    className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-overline text-zinc-500">
                    WhatsApp / phone (optional)
                  </span>
                  <div className="mt-2 flex border border-zinc-300 focus-within:border-electric relative">
                    <button
                      type="button"
                      onClick={() => setCountryOpen((s) => !s)}
                      className="inline-flex items-center gap-2 px-3 border-r border-zinc-300 text-sm font-medium text-charcoal hover:bg-zinc-50"
                      aria-haspopup="listbox"
                      aria-expanded={countryOpen}
                      data-testid="contact-country-trigger"
                    >
                      <span aria-hidden>{country.flag}</span>
                      <span className="font-mono">{country.dial}</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 ${countryOpen ? "rotate-180 text-electric" : "text-zinc-400"} transition-transform`}
                      />
                    </button>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={onChange("phone")}
                      placeholder="98765 43210"
                      data-testid="contact-input-phone"
                      className="flex-1 px-4 py-3 text-sm outline-none min-w-0"
                    />
                    {countryOpen && (
                      <ul
                        role="listbox"
                        className="absolute left-0 top-full mt-1 w-72 max-h-72 overflow-y-auto bg-white border border-charcoal shadow-xl shadow-electric/20 rounded-xl z-30"
                        data-testid="contact-country-list"
                      >
                        {COUNTRIES.map((c) => (
                          <li key={c.code}>
                            <button
                              type="button"
                              onClick={() => {
                                setCountry(c);
                                setCountryOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-zinc-50 ${c.code === country.code ? "bg-zinc-50" : ""}`}
                            >
                              <span>{c.flag}</span>
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="font-mono text-xs text-zinc-500">{c.dial}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-overline text-zinc-500">
                    App name or URL
                  </span>
                  <input
                    type="text"
                    value={form.app_name}
                    onChange={onChange("app_name")}
                    data-testid="contact-input-app"
                    placeholder="MealFox · or App Store URL"
                    className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-overline text-zinc-500">Message</span>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="contact-input-message"
                    placeholder="What are you trying to rank? What's blocking you?"
                    className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none resize-none"
                  />
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                aria-label="Send contact message"
                className="mt-6 inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send message <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Sidebar */}
            <aside className="lg:col-span-5 space-y-6">
              <div className="bg-charcoal text-white p-8 shadow-xl shadow-electric/20 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-electric" />
                  <span className="text-overline text-electric">Book a call</span>
                </div>
                <p className="font-display text-2xl font-bold leading-tight">
                  30-minute ASO strategy call
                </p>
                <p className="mt-3 text-zinc-300 text-sm leading-relaxed">
                  Walk through your app, your market and your growth blockers
                  with an Lumotis senior strategist. Free, no obligation.
                </p>
                <a
                  href="https://calendly.com/lumotis/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-calendly"
                  className="mt-5 inline-flex items-center gap-2 bg-electric text-white px-5 py-3 font-semibold hover:bg-white hover:text-charcoal transition-colors"
                >
                  Open Calendly <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="bg-white border border-zinc-200 p-8 space-y-5">
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-electric mt-1" />
                  <div>
                    <p className="text-overline text-zinc-500">Email</p>
                    <a
                      href="mailto:hello@lumotis.com"
                      className="font-semibold text-charcoal hover:text-electric"
                      data-testid="contact-email-link"
                    >
                      hello@lumotis.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-electric mt-1" />
                  <div>
                    <p className="text-overline text-zinc-500">Sales</p>
                    <a
                      href="mailto:sales@lumotis.com"
                      className="font-semibold text-charcoal hover:text-electric"
                    >
                      sales@lumotis.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-electric mt-1" />
                  <div>
                    <p className="text-overline text-zinc-500">HQ</p>
                    <p className="text-charcoal font-medium">
                      Jaipur, Rajasthan, India
                    </p>
                    <p className="text-sm text-zinc-500">
                      Lumotis Digital Media Pvt. Ltd.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
                  <a
                    href="https://linkedin.com/company/lumotis"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="w-9 h-9 grid place-items-center border border-zinc-300 hover:bg-electric hover:text-white hover:border-electric transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a
                    href="https://twitter.com/lumotis"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                    className="w-9 h-9 grid place-items-center border border-zinc-300 hover:bg-electric hover:text-white hover:border-electric transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
